import React, { useState } from 'react';
import { captureError } from '@/utils/captureError';
import { Download, Upload, Trash2 } from 'lucide-react';
import { collection, getDocs, writeBatch, doc, deleteDoc } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { motion, AnimatePresence } from 'motion/react';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useProfile } from '@/context/ProfileContext';
import { useInventory } from '@/context/InventoryContext';
import { db, storage, auth } from '@/firebase';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import {
  Card,
  Row,
  Heading,
  Body,
  Button,
  Input,
  InlineMessage,
  showToast,
} from '@/components/rc';
import { trackEvent } from '@/config/analytics';
import { markAccountDeletionInProgress, clearAccountDeletionInProgress } from '@/utils/accountDeletion';
import { canPurchaseInApp } from '@/utils/purchaseAvailability';
import { openManageSubscription } from '@/utils/playBilling';
import { exportCellarCSV } from '@/utils/exportCellar';
import ImportFlow from '@/components/import/ImportFlow';

// ── Component ──

const DangerZoneSection: React.FC = () => {
  const { hasSubscription, profile } = useProfile();
  const { inventory } = useInventory();

  // A Play subscription is billed by Google against the user's Play account,
  // not by us — there is no server-side cancel we can call, and passing a
  // Play transaction id to Stripe's cancelSubscription always fails. So the
  // Stripe step is skipped and the modal tells them to cancel in Play
  // instead. Deletion must still go through either way: Play's policy
  // requires in-app account deletion, and refusing to delete until an
  // external subscription is cancelled would not satisfy it.
  const playSubscriber = hasSubscription && profile.subscriptionSource === 'play';

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const handleExport = async () => {
    trackEvent('csv_exported');
    await exportCellarCSV(inventory);
    showToast({ tone: 'neutral', message: 'Cellar exported' });
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError(null);

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setDeleting(false);
      setDeleteError("You're signed out. Please sign in again.");
      return;
    }
    const uid = currentUser.uid;

    // Firebase requires a recent sign-in (~5 min) to delete an account.
    // Check up front — hitting auth/requires-recent-login after the data
    // sweeps would leave the account alive with the cellar already gone.
    try {
      const token = await currentUser.getIdTokenResult();
      if (Date.now() - Date.parse(token.authTime) > 4 * 60 * 1000) {
        setDeleting(false);
        setDeleteError('For security, please sign out and sign back in, then try again.');
        return;
      }
    } catch {
      // Token inspection failed — fall through and let delete() decide.
    }

    try {
      // Step 0: Cancel Stripe subscription if active. Abort on failure —
      // proceeding would destroy the account's data while Stripe keeps
      // billing a subscription nobody can manage any more. Play-sourced
      // subscriptions skip this entirely (see playSubscriber above).
      if (hasSubscription && !playSubscriber) {
        try {
          const res = await authFetch(FUNCTION_URLS.cancelSubscription, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!res.ok) throw new Error(`cancelSubscription ${res.status}`);
        } catch (e) {
          setDeleting(false);
          setDeleteError(
            canPurchaseInApp()
              ? 'Could not cancel your subscription. Try again, or cancel it under Manage Subscription first.'
              : 'Could not cancel your subscription. Please try again.',
          );
          captureError(e, { context: 'account_delete_cancel' });
          return;
        }
      }

      markAccountDeletionInProgress();

      // Step 1: Delete all wine documents (user still authenticated for security rules)
      const winesRef = collection(db, 'users', uid, 'wines');
      const snapshot = await getDocs(winesRef);
      const BATCH_SIZE = 500;
      let batch = writeBatch(db);
      let count = 0;
      for (const docSnap of snapshot.docs) {
        batch.delete(docSnap.ref);
        count++;
        if (count % BATCH_SIZE === 0) {
          await batch.commit();
          batch = writeBatch(db);
        }
      }
      if (count % BATCH_SIZE !== 0) {
        await batch.commit();
      }

      // Step 1b: Delete FCM tokens (owner-writable per rules). Usage docs are
      // server-write-only — deleting them would let users reset monthly AI
      // caps — so they stay behind as unreachable orphans.
      try {
        const tokensSnap = await getDocs(collection(db, 'users', uid, 'fcmTokens'));
        const tokenBatch = writeBatch(db);
        tokensSnap.docs.forEach(t => tokenBatch.delete(t.ref));
        if (!tokensSnap.empty) await tokenBatch.commit();
      } catch (e) {
        console.warn('FCM token cleanup:', e);
      }

      // Step 2: Delete Storage files
      try {
        const labelsRef = ref(storage, `users/${uid}/labels`);
        const labelsList = await listAll(labelsRef);
        await Promise.all(labelsList.items.map(item => deleteObject(item)));
      } catch (e) {
        console.warn('Label cleanup:', e);
      }

      try {
        const winelistsRef = ref(storage, `users/${uid}/winelists`);
        const wlList = await listAll(winelistsRef);
        for (const prefix of wlList.prefixes) {
          const subList = await listAll(prefix);
          await Promise.all(subList.items.map(item => deleteObject(item)));
        }
        await Promise.all(wlList.items.map(item => deleteObject(item)));
      } catch (e) {
        console.warn('Wine list cleanup:', e);
      }

      try {
        const feedbackRef = ref(storage, `users/${uid}/feedback`);
        const fbList = await listAll(feedbackRef);
        await Promise.all(fbList.items.map(item => deleteObject(item)));
      } catch (e) {
        console.warn('Feedback image cleanup:', e);
      }

      // Step 3: Delete the profile document — last data op, immediately
      // before auth teardown, keeping the ProfileContext bootstrap window
      // (see utils/accountDeletion.ts) as narrow as possible.
      await deleteDoc(doc(db, 'users', uid, 'profile', 'preferences'));

      // Step 4: Tear down auth. Provider-layer sign-out first — the web-SDK
      // delete() fires onAuthStateChanged(null) and unmounts this tree, so
      // anything after it is not guaranteed to run. Always delete via the
      // web SDK: under skipNativeAuth the session lives there, and the
      // plugin's deleteUser() acts on a native instance that has no user.
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut().catch(() => {});
      }
      await currentUser.delete();
    } catch (e: any) {
      if (e?.code === 'auth/requires-recent-login') {
        setDeleteError('For security, please sign out and sign back in, then try again.');
      } else {
        setDeleteError(`Failed to delete account: ${e?.message || 'Unknown error'}`);
        captureError(e, { context: 'account_delete' });
      }
      setDeleting(false);
    } finally {
      clearAccountDeletionInProgress();
    }
  };

  return (
    <>
      <Heading scale="subhead" className="mb-2">Data</Heading>
      <Card elevation="flat">
        <Row
          title="Import Wines"
          subtitle="From CellarTracker, Vivino, or CSV"
          leadingIcon={<Upload size={20} />}
          trailingAction="chevron"
          onClick={() => setShowImport(true)}
        />
        <Row
          title="Export Cellar (CSV)"
          leadingIcon={<Download size={20} />}
          trailingAction="chevron"
          onClick={handleExport}
        />
        <Row
          title="Delete Account"
          leadingIcon={<Trash2 size={20} className="text-[var(--rc-accent-coral)]" />}
          trailingAction="chevron"
          onClick={() => { setDeleteModalOpen(true); setConfirmText(''); setDeleteError(null); }}
          divider={false}
          className="[&_span:first-of-type]:text-[var(--rc-accent-coral)]"
        />
      </Card>

      {/* ── Delete Account Modal ── */}
      <AnimatePresence>
        {deleteModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
            onClick={(e) => { if (e.target === e.currentTarget && !deleting) setDeleteModalOpen(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="w-full max-w-[440px]"
            >
              <Card elevation="raised" padding="standard">
                <Heading scale="heading" colour="accent-coral">Delete Account</Heading>
                <div className="h-3" />
                <Body className="w-auto">
                  This will permanently delete your account and all cellar data. This cannot be undone.
                </Body>
                {playSubscriber && (
                  <>
                    <div className="h-3" />
                    <InlineMessage
                      tone="warning"
                      message="Your subscription is billed by Google Play and won't be cancelled by deleting your account. Cancel it in Google Play first, or you'll keep being charged."
                    />
                    <div className="h-2" />
                    <Button
                      variantType="Tertiary"
                      label="Cancel in Google Play"
                      onClick={() => { void openManageSubscription(); }}
                      disabled={deleting}
                    />
                  </>
                )}
                <div className="h-4" />
                <Body className="w-auto">Type DELETE to confirm:</Body>
                <div className="h-2" />
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  state={deleting ? 'Disabled' : 'Default'}
                />
                <div className="h-4" />
                <InlineMessage tone="warning" message="This action is irreversible" />
                {deleteError && (
                  <>
                    <div className="h-3" />
                    <InlineMessage tone="error" message={deleteError} />
                  </>
                )}
                <div className="h-4" />
                <div className="flex gap-3">
                  <Button
                    variantType="Secondary"
                    label="Cancel"
                    onClick={() => setDeleteModalOpen(false)}
                    disabled={deleting}
                    className="flex-1"
                  />
                  <Button
                    variantType="Destructive"
                    label={deleting ? 'Deleting...' : 'Delete Everything'}
                    disabled={confirmText !== 'DELETE' || deleting}
                    onClick={handleDelete}
                    className="flex-1"
                  />
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showImport && <ImportFlow onClose={() => setShowImport(false)} />}
    </>
  );
};

export default DangerZoneSection;
